/**
 * Pixeldichte-Erkennung: der Schritt von Browser-Pixeln zu echten Zentimetern.
 *
 * Ohne diesen Schritt sind alle Zahlen der App wertlos - dieselbe Wischbewegung
 * ergaebe auf jedem Geraet ein anderes Ergebnis, und eine Bestenliste waere
 * reine Fiktion.
 *
 * Reihenfolge der Verfahren, absteigend nach Verlaesslichkeit:
 *   1. Manuelle Kalibrierung des Nutzers (falls vorhanden) - exakt
 *   2. Geraetemodell aus User-Agent Client Hints - eindeutig, nur Android
 *   3. Hardware-Aufloesung gegen die Geraete-Datenbank - bei iPhones sehr gut
 *   4. Heuristik ueber typische Displaybreite je Geraeteklasse - grob
 *
 * Die erreichte Stufe wird als `confidence` mitgefuehrt und in der Oberflaeche
 * offengelegt. Ein "geschaetzt"-Ergebnis darf nicht wie ein gemessenes aussehen.
 */

import {
  FALLBACK_SCREEN_WIDTH_INCHES,
  PPI_BY_MODEL,
  PPI_BY_RESOLUTION,
} from './devices'

export type PpiConfidence = 'calibrated' | 'model' | 'resolution' | 'estimated'

export interface DeviceMetrics {
  /** Erkannte oder geschaetzte Pixeldichte in Pixeln pro Zoll. */
  ppi: number
  /** Verhaeltnis Hardware- zu CSS-Pixeln. */
  dpr: number
  /** Wie verlaesslich die ppi-Angabe ist. */
  confidence: PpiConfidence
  /** Aufloesung im Hochformat als "breite x hoehe", Hardware-Pixel. */
  resolutionKey: string
  /** Erkanntes Geraetemodell, falls der Browser es preisgibt. */
  model: string | null
  /** Geraeteklasse, wie sie fuer die Heuristik bestimmt wurde. */
  deviceClass: 'phone' | 'tablet' | 'desktop'
  /** Fuer die Anzeige aufbereitete Begruendung der Erkennung. */
  explanation: string
}

const CALIBRATION_KEY = 'som.calibration.v1'

/** Plausibilitaetsgrenzen. Ausserhalb davon ist ein Wert sicher falsch. */
const MIN_PPI = 80
const MAX_PPI = 800

/**
 * Ermittelt die Geraetekennwerte. Asynchron, weil die Client Hints
 * (getHighEntropyValues) ein Promise liefern.
 */
export async function detectDeviceMetrics(): Promise<DeviceMetrics> {
  const dpr = window.devicePixelRatio || 1
  const deviceClass = classifyDevice()
  const { resolutionKey, physicalWidth } = readResolution(dpr)
  const model = await readDeviceModel()

  // Stufe 1: Der Nutzer hat selbst kalibriert. Schlaegt alles andere.
  const calibrated = loadCalibration()
  if (calibrated !== null) {
    return {
      ppi: calibrated,
      dpr,
      confidence: 'calibrated',
      resolutionKey,
      model,
      deviceClass,
      explanation: 'Von dir selbst kalibriert.',
    }
  }

  // Stufe 2: Geraetemodell aus den Client Hints.
  if (model) {
    const key = normaliseModel(model)
    const byModel = PPI_BY_MODEL[key]
    if (byModel && isPlausiblePpi(byModel)) {
      return {
        ppi: byModel,
        dpr,
        confidence: 'model',
        resolutionKey,
        model,
        deviceClass,
        explanation: `Geraet als ${model} erkannt.`,
      }
    }
  }

  // Stufe 3: Aufloesung gegen die Datenbank.
  const byResolution = PPI_BY_RESOLUTION[resolutionKey]
  if (byResolution && isPlausiblePpi(byResolution)) {
    return {
      ppi: byResolution,
      dpr,
      confidence: 'resolution',
      resolutionKey,
      model,
      deviceClass,
      explanation: `Aufloesung ${resolutionKey} in der Geraeteliste gefunden.`,
    }
  }

  // Stufe 4: Heuristik.
  const estimated = estimatePpi(deviceClass, physicalWidth, dpr)
  return {
    ppi: estimated,
    dpr,
    confidence: 'estimated',
    resolutionKey,
    model,
    deviceClass,
    explanation:
      deviceClass === 'desktop'
        ? 'Desktop-Browser - Rechnung nach CSS-Standard mit 96 dpi.'
        : `Unbekanntes Geraet (${resolutionKey}) - ueber typische Displaybreite geschaetzt.`,
  }
}

/**
 * Aufloesung im Hochformat. Normalisiert auf Hochformat, damit ein gedrehtes
 * Geraet nicht als anderes Modell durchgeht.
 */
function readResolution(dpr: number): { resolutionKey: string; physicalWidth: number } {
  const cssW = window.screen.width
  const cssH = window.screen.height
  const physW = Math.round(Math.min(cssW, cssH) * dpr)
  const physH = Math.round(Math.max(cssW, cssH) * dpr)
  return { resolutionKey: `${physW}x${physH}`, physicalWidth: physW }
}

/**
 * Geraetemodell ueber die User-Agent Client Hints.
 * Nur Chromium auf Android liefert hier etwas; ueberall sonst null.
 */
async function readDeviceModel(): Promise<string | null> {
  const uaData = (navigator as Navigator & { userAgentData?: NavigatorUAData }).userAgentData
  if (!uaData?.getHighEntropyValues) return null
  try {
    const values = await uaData.getHighEntropyValues(['model'])
    const model = values.model?.trim()
    return model ? model : null
  } catch {
    // Die Abfrage kann von der Permissions-Policy blockiert sein. Kein Drama,
    // dann greift die naechste Stufe.
    return null
  }
}

function classifyDevice(): 'phone' | 'tablet' | 'desktop' {
  const ua = navigator.userAgent
  const coarsePointer = window.matchMedia('(pointer: coarse)').matches
  const isIpad =
    /iPad/.test(ua) ||
    // iPadOS gibt sich seit Version 13 als macOS aus; Touchpunkte verraten es.
    (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1)

  if (isIpad || /Tablet|PlayBook|Silk/.test(ua)) return 'tablet'
  if (/Mobi|Android|iPhone|iPod/.test(ua)) return 'phone'
  if (coarsePointer && Math.min(window.screen.width, window.screen.height) < 600) return 'phone'
  return 'desktop'
}

/**
 * Schaetzt die Pixeldichte ueber die typische physikalische Displaybreite der
 * Geraeteklasse. Fuer Desktops gilt stattdessen der CSS-Standard: ein CSS-Pixel
 * ist dort per Definition 1/96 Zoll, devicePixelRatio deckt HiDPI ab.
 */
function estimatePpi(
  deviceClass: 'phone' | 'tablet' | 'desktop',
  physicalWidth: number,
  dpr: number,
): number {
  if (deviceClass === 'desktop') return clampPpi(96 * dpr)
  const inches = FALLBACK_SCREEN_WIDTH_INCHES[deviceClass]
  return clampPpi(physicalWidth / inches)
}

function isPlausiblePpi(ppi: number): boolean {
  return Number.isFinite(ppi) && ppi >= MIN_PPI && ppi <= MAX_PPI
}

function clampPpi(ppi: number): number {
  if (!Number.isFinite(ppi)) return 96
  return Math.min(MAX_PPI, Math.max(MIN_PPI, ppi))
}

function normaliseModel(model: string): string {
  return model.toLowerCase().replace(/[\s_]/g, '')
}

// --- Manuelle Kalibrierung ------------------------------------------------

/**
 * Die Notbremse fuer falsch erkannte Geraete: Der Nutzer legt eine EC-Karte auf
 * den Bildschirm und zieht ein Rechteck darunter passend.
 *
 * Warum ausgerechnet eine Karte: Das Format ID-1 nach ISO/IEC 7810 misst
 * weltweit exakt 85,60 x 53,98 mm - bei Bankkarte, Krankenkassenkarte,
 * Fuehrerschein und Personalausweis gleichermassen. Einen so genauen Massstab
 * hat sonst kaum jemand in Reichweite.
 *
 * Kalibriert wird ueber die *kurze* Seite. Die lange Seite ist mit 8,56 cm
 * breiter als ein Handy-Display im Hochformat und wuerde nicht draufpassen.
 */
export const REFERENCE_CARD_SHORT_CM = 5.398
export const REFERENCE_CARD_LONG_CM = 8.56

/** Rechnet die abgemessene kurze Kartenseite in eine Pixeldichte um. */
export function ppiFromCardShortSide(shortSideCssPx: number, dpr: number): number {
  const inches = REFERENCE_CARD_SHORT_CM / 2.54
  return clampPpi((shortSideCssPx * dpr) / inches)
}

/** Umgekehrt: Wie breit muesste die kurze Kartenseite bei dieser Dichte sein? */
export function cardShortSidePx(ppi: number, dpr: number): number {
  return (REFERENCE_CARD_SHORT_CM / 2.54) * (ppi / dpr)
}

export function saveCalibration(ppi: number): void {
  try {
    localStorage.setItem(CALIBRATION_KEY, String(ppi))
  } catch {
    // Privater Modus ohne Speicherzugriff - die Kalibrierung gilt dann nur
    // fuer die laufende Sitzung. Kein Grund, die App anzuhalten.
  }
}

export function loadCalibration(): number | null {
  try {
    const raw = localStorage.getItem(CALIBRATION_KEY)
    if (!raw) return null
    const value = Number(raw)
    return isPlausiblePpi(value) ? value : null
  } catch {
    return null
  }
}

export function clearCalibration(): void {
  try {
    localStorage.removeItem(CALIBRATION_KEY)
  } catch {
    // s. o.
  }
}

/** Klartext fuer die Oberflaeche. */
export function confidenceLabel(confidence: PpiConfidence): string {
  switch (confidence) {
    case 'calibrated':
      return 'kalibriert'
    case 'model':
      return 'Geraet erkannt'
    case 'resolution':
      return 'Aufloesung erkannt'
    case 'estimated':
      return 'geschaetzt'
  }
}

/**
 * Wie genau ist die Messung auf diesem Geraet, in Worten?
 *
 * Frueher entschied die Erkennungsstufe darueber, ob ein Ergebnis ueberhaupt
 * gewertet wird. Das ist entfallen: Ein unbekanntes Geraetemodell ist kein
 * Fehlverhalten, und bei Android betraf es die Mehrheit der Spieler. Die Stufe
 * ist jetzt reine Information - und eine Einladung zum Kalibrieren, keine
 * Huerde.
 */
export function accuracyHint(confidence: PpiConfidence): string | null {
  if (confidence !== 'estimated') return null
  return 'Dein Gerätemodell ist unbekannt, der Maßstab daher geschätzt. Für genauere Zentimeter einmal kalibrieren.'
}

// Client Hints sind noch nicht in den Standard-Typdefinitionen von TypeScript.
interface NavigatorUAData {
  getHighEntropyValues?: (hints: string[]) => Promise<{ model?: string }>
}
